import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";

export interface UserSubscription {
  id: string | null;
  user_id: string;
  plan: "free" | "kairos";
  status: "free" | "trial" | "active" | "trialing" | "pending" | "past_due" | "canceled" | "expired";
  payfast_token?: string;
  payfast_payment_id?: string;
  pf_payment_id?: string;
  trial_ends_at?: string;
  current_period_start?: string;
  current_period_end?: string;
  canceled_at?: string;
  free_insights_used: Record<string, string | string[]>;
  created_at: string | null;
  updated_at: string | null;
  is_effective_active?: boolean;
  access_source?: "subscription" | "free_pass" | "church_seat" | "none";
}

export function useKairosSub(userId?: string) {
  return useQuery({
    queryKey: ["kairos-subscription", userId],
    queryFn: async (): Promise<UserSubscription | null> => {
      if (!userId) return null;

      // Use getUser() which validates the token server-side and triggers refresh if needed,
      // then get the (potentially refreshed) session for the access token.
      await supabase.auth.getUser();
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const baseURL = (import.meta.env.VITE_API_BASE as string) || "";
      const res = await fetch(`${baseURL}/api/billing/individual/subscription`, {
        headers: {
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {}),
        },
      });

      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload?.error || "Failed to load subscription");
      }

      return payload?.subscription ?? null;
    },
    enabled: !!userId,
  });
}

/** Is the subscription currently valid? */
export function isKairosActive(sub: UserSubscription | null | undefined): boolean {
  if (!sub) return false;
  if (typeof sub.is_effective_active === "boolean") return sub.is_effective_active;
  if (sub.status === "active") return true;
  if (sub.status === "trial" && sub.trial_ends_at) {
    return new Date(sub.trial_ends_at) > new Date();
  }
  if (sub.status === "trialing" && sub.trial_ends_at) {
    return new Date(sub.trial_ends_at) > new Date();
  }
  return false;
}

/** Has the user used their free taste for a given feature? */
export function hasUsedFreeTaste(
  sub: UserSubscription | null | undefined,
  feature: string
): boolean {
  if (!sub) return false;
  return !!sub.free_insights_used?.[feature];
}

/** Start individual PayFast checkout after the free trial ends. */
export async function startKairosCheckout(): Promise<{
  paymentUrl: string;
  fields: Record<string, string>;
}> {
  await supabase.auth.getUser();
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const baseURL = (import.meta.env.VITE_API_BASE as string) || "";
  const res = await fetch(`${baseURL}/api/billing/individual/checkout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {}),
    },
  });
  const data = await res.json();
  if (!res.ok || !data?.paymentUrl) {
    if (data?.detail) console.error("[checkout] server detail:", data.detail);
    throw new Error(data?.error || "Failed to create checkout");
  }
  return { paymentUrl: data.paymentUrl, fields: data.fields };
}

/** Cancel the user's individual Kairos subscription (PayFast recurring) */
export async function cancelKairosSubscription(): Promise<{ ok: boolean }> {
  await supabase.auth.getUser();
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const baseURL = (import.meta.env.VITE_API_BASE as string) || "";
  const res = await fetch(`${baseURL}/api/billing/individual/cancel`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {}),
    },
  });
  const data = await res.json();
  if (!res.ok || !data?.ok) {
    if (data?.detail) console.error("[cancel] server detail:", data.detail);
    throw new Error(data?.error || "Failed to cancel subscription");
  }
  return { ok: true };
}

/** Submit hidden form to PayFast (browser POST redirect) */
export function submitPayFastForm(
  paymentUrl: string,
  fields: Record<string, string>
): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = paymentUrl;
  for (const [key, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = String(value);
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Submit a pastor referral for 7 free days */
export async function submitPastorReferral(args: {
  pastorName: string;
  pastorEmail: string;
  churchName?: string;
}): Promise<{ ok: boolean; message: string; rewardDays: number }> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const baseURL = (import.meta.env.VITE_API_BASE as string) || "";
  const res = await fetch(`${baseURL}/api/billing/referral`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {}),
    },
    body: JSON.stringify(args),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || "Referral failed");
  return data;
}
